import parsePhoneNumber, { AsYouType, getCountries, getCountryCallingCode, getExampleNumber, } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
const supportedCountries = new Set(getCountries());
export function normalizePhoneCountry(country) {
    const normalized = country?.trim().toUpperCase();
    if (!normalized || !supportedCountries.has(normalized)) {
        return undefined;
    }
    return normalized;
}
export function coercePhoneInput(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const input = String(value).trim();
    return input.length ? input : null;
}
export function tryNormalizePhoneNumber(value, defaultCountry) {
    const input = coercePhoneInput(value);
    if (input === null) {
        return null;
    }
    const phoneNumber = parsePhoneNumber(input, normalizePhoneCountry(defaultCountry));
    if (!phoneNumber?.isPossible()) {
        return undefined;
    }
    return phoneNumber.number;
}
export function isPossiblePhoneNumberValue(value, defaultCountry) {
    return tryNormalizePhoneNumber(value, defaultCountry) !== undefined;
}
export function formatPhoneNumberForDisplay(value) {
    const input = coercePhoneInput(value);
    if (!input) {
        return "";
    }
    const phoneNumber = parsePhoneNumber(input);
    return phoneNumber?.formatInternational() ?? input;
}
export function formatPhoneNumberForInput(value, defaultCountry) {
    const input = coercePhoneInput(value);
    if (!input) {
        return "";
    }
    const country = normalizePhoneCountry(defaultCountry);
    const phoneNumber = parsePhoneNumber(input, country);
    if (phoneNumber &&
        country &&
        (phoneNumber.country === country ||
            phoneNumber.countryCallingCode === getCountryCallingCode(country))) {
        return phoneNumber.formatNational();
    }
    return new AsYouType(country).input(input);
}
export function getNationalPhoneDigits(value, defaultCountry) {
    const input = coercePhoneInput(value);
    if (!input) {
        return "";
    }
    const country = normalizePhoneCountry(defaultCountry);
    const digits = input.replace(/\D/g, "");
    const phoneNumber = parsePhoneNumber(input, country);
    if (phoneNumber &&
        country &&
        phoneNumber.isPossible() &&
        phoneNumberMatchesCountry(phoneNumber, country)) {
        return phoneNumber.nationalNumber;
    }
    if (country) {
        const dialCode = getCountryCallingCode(country);
        const nationalCandidate = digits.startsWith(dialCode)
            ? digits.slice(dialCode.length)
            : "";
        const candidatePhoneNumber = nationalCandidate
            ? parsePhoneNumber(nationalCandidate, country)
            : undefined;
        if (candidatePhoneNumber?.isPossible()) {
            return nationalCandidate;
        }
    }
    return digits;
}
export function getPhoneMaskPlaceholder(defaultCountry) {
    const maskTemplate = getPhoneMaskTemplate(defaultCountry);
    if (!maskTemplate) {
        return "Número nacional";
    }
    return maskTemplate.replace(/#/g, "0");
}
export function formatNationalPhoneMask(value, defaultCountry) {
    const nationalDigits = getNationalPhoneDigits(value, defaultCountry);
    const maskTemplate = getPhoneMaskTemplate(defaultCountry);
    if (!nationalDigits || !maskTemplate) {
        return nationalDigits;
    }
    return applyPhoneMask(nationalDigits, maskTemplate);
}
function getPhoneMaskTemplate(defaultCountry) {
    const country = normalizePhoneCountry(defaultCountry);
    if (!country) {
        return "";
    }
    return (getExampleNumber(country, examples)?.formatNational().replace(/\d/g, "#") ??
        "");
}
export function getPhoneCountryFromValue(value, fallbackCountry = "CR") {
    const fallback = normalizePhoneCountry(fallbackCountry) ?? "CR";
    const input = coercePhoneInput(value);
    if (!input) {
        return fallback;
    }
    const phoneNumber = parsePhoneNumber(input);
    return phoneNumber?.country ?? fallback;
}
export function getPhoneCountryOptions(locale = "es") {
    const displayNames = typeof Intl !== "undefined" && "DisplayNames" in Intl
        ? new Intl.DisplayNames([locale], { type: "region" })
        : null;
    return getCountries()
        .map((country) => {
        const name = displayNames?.of(country) ?? country;
        const dialCode = getCountryCallingCode(country);
        return {
            iso2: country,
            name,
            dialCode,
            label: `${name} +${dialCode}`,
        };
    })
        .sort((a, b) => a.name.localeCompare(b.name, locale));
}
export function formatPhoneNumberAsYouType(value, defaultCountry) {
    const input = coercePhoneInput(value);
    if (!input) {
        return "";
    }
    const country = normalizePhoneCountry(defaultCountry);
    const phoneNumber = parsePhoneNumber(input, country);
    if (phoneNumber &&
        country &&
        phoneNumber.isPossible() &&
        phoneNumberMatchesCountry(phoneNumber, country)) {
        return phoneNumber.formatNational();
    }
    return new AsYouType(country).input(input);
}
function phoneNumberMatchesCountry(phoneNumber, country) {
    return (phoneNumber?.country === country ||
        phoneNumber?.countryCallingCode === getCountryCallingCode(country));
}
function applyPhoneMask(digits, maskTemplate) {
    let output = "";
    let digitIndex = 0;
    for (const character of maskTemplate) {
        if (character === "#") {
            if (digitIndex >= digits.length) {
                break;
            }
            output += digits[digitIndex];
            digitIndex += 1;
            continue;
        }
        if (digitIndex < digits.length) {
            output += character;
        }
    }
    return output + digits.slice(digitIndex);
}
